import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LabelList } from 'recharts';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const BiometricEfficiencyChart = ({ filters }) => {
  const { accessToken } = useAuth();
  const [chartData, setChartData] = useState([]);
  const [statistics, setStatistics] = useState({
    average: 0,
    maxClient: null,
    minClient: null,
  });
  const [loading, setLoading] = useState(true);
  const [focusMode, setFocusMode] = useState(false);

  const buildParams = (filters) => {
    const params = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params[key] = filters[key];
    });
    return params;
  };

  useEffect(() => {
    const fetchDayWiseEfficiency = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const resp = await api.get(`${baseUrl}//superAdminDashboard/getWeeklyWiseBiometricefficiency`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: buildParams(filters),
        });

        if (resp.data.success) {
          const raw = resp.data.dayWiseEfficiency || [];
          const formatted = raw.map((item) => {
            const [year, month, day] = item.date.split('-'); // assuming input is YYYY-MM-DD
            return {
              date: `${month}/${day}/${year}`,
              efficiency: parseFloat(item.avgEfficiencyPercentage.replace('%', '')),
            };
          });

          setChartData(formatted);

          if (formatted.length) {
            const vals = formatted.map((d) => d.efficiency);
            const avg = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
            const max = Math.max(...vals);
            const min = Math.min(...vals);
            setStatistics({
              average: avg,
              maxClient: formatted.find((d) => d.efficiency === max),
              minClient: formatted.find((d) => d.efficiency === min),
            });
          } else {
            setStatistics({ average: 0, maxClient: null, minClient: null });
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchDayWiseEfficiency();
  }, [accessToken, filters]);

  const ChartContent = () => (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#808080" />
        <XAxis
          dataKey="date"
          tick={{
            fill: '#1A2B5C',
            fontWeight: 600,
            fontSize: 12,
          }}
          angle={-35}
          textAnchor="end"
          interval={0}
          axisLine={{ stroke: '#808080' }}
          label={{
            value: 'Date',
            position: 'bottom',
            dy: 30,
            style: { fontWeight: 'bold', fill: '#1A2B5C', fontSize: 14 },
          }}
        />

        <YAxis
          tick={{ fill: '#1A2B5C', fontWeight: 600 }}
          axisLine={{ stroke: '#808080' }}
          label={{
            value: 'Efficiency %',
            angle: -90,
            position: 'insideLeft',
            dy: 20,
            style: { fontWeight: 'bold', fill: '#1A2B5C', fontSize: 14 },
          }}
          domain={[0, 100]}
        />
        <Tooltip
          contentStyle={{ backgroundColor: 'white', borderColor: '#808080' }}
          itemStyle={{ color: '#1A2B5C', fontWeight: 600 }}
          formatter={(val) => `${val}%`}
        />
        <Line
          type="monotone"
          dataKey="efficiency"
          stroke="#EE1D23"
          strokeWidth={3}
          dot={{ r: 5, stroke: '#1A2B5C', strokeWidth: 2, fill: 'white' }}
          activeDot={{ r: 7, stroke: '#1A2B5C', strokeWidth: 3, fill: '#EE1D23' }}
        >
          <LabelList
            dataKey="efficiency"
            content={({ x, y, value }) => (
              <text
                x={x + 12}
                y={y - 10} // shift label 10px above the point
                fill="#1A2B5C"
                fontSize={12}
                fontWeight="bold"
                textAnchor="middle"
              >
                {`${Math.round(value)}%`}
              </text>
            )}
          />
        </Line>
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <>
      <div className="bg-white rounded-xl shadow-md p-6 mt-10 relative">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-[#1A2B5C]">Day‑wise Biometric Efficiency</h2>
          <button
            onClick={() => setFocusMode(true)}
            className="px-4 py-1.5 text-sm rounded-md bg-[#1A2B5C] text-white hover:bg-[#EE1D23] transition"
          >
            Focus Mode
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-[#1A2B5C]" />
          </div>
        ) : (
          <>
            {/* === STATS CARDS === */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#F0F4FA' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Average Efficiency
                </p>
                <p className="text-2xl font-bold" style={{ color: '#1A2B5C' }}>
                  {statistics.average ? `${Number(statistics.average).toFixed(0)}%` : '--'}
                </p>
              </div>
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#EAF4F4' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Highest Efficiency
                </p>
                <p className="text-lg font-medium" style={{ color: '#1A2B5C' }}>
                  {statistics.maxClient?.date ?? '--'}
                </p>
                <p className="text-xl font-bold" style={{ color: '#1A2B5C' }}>
                  {statistics.maxClient?.efficiency ? `${Number(statistics.maxClient.efficiency).toFixed(0)}%` : '--'}
                </p>
              </div>
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#FDEBEB' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Lowest Efficiency
                </p>
                <p className="text-lg font-medium" style={{ color: '#EE1D23' }}>
                  {statistics.minClient?.date ?? '--'}
                </p>
                <p className="text-xl font-bold" style={{ color: '#EE1D23' }}>
                  {statistics.minClient?.efficiency !== null && statistics.minClient?.efficiency !== undefined
                    ? `${Number(statistics.minClient.efficiency).toFixed(0)}%`
                    : '--'}
                </p>
              </div>
            </div>

            {/* === CHART === */}
            <ChartContent />
          </>
        )}
      </div>

      {focusMode && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative w-screen min-h-screen bg-white rounded-3xl p-8 shadow-2xl overflow-y-auto">
            <button
              onClick={() => setFocusMode(false)}
              className="absolute top-4 right-4 bg-white rounded-full shadow p-2 hover:text-[#EE1D23] transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold mb-6 text-center text-[#1A2B5C]">Day‑wise Biometric Efficiency (Focus Mode)</h2>
            {/* Stats in focus */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#F0F4FA' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Average Efficiency
                </p>
                <p className="text-2xl font-bold" style={{ color: '#1A2B5C' }}>
                  {statistics.average ? `${Number(statistics.average).toFixed(0)}%` : '--'}
                </p>
              </div>
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#EAF4F4' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Highest Efficiency
                </p>
                <p className="text-lg font-medium" style={{ color: '#1A2B5C' }}>
                  {statistics.maxClient?.date ?? '--'}
                </p>
                <p className="text-xl font-bold" style={{ color: '#1A2B5C' }}>
                  {statistics.maxClient?.efficiency ? `${Number(statistics.maxClient.efficiency).toFixed(0)}%` : '--'}
                </p>
              </div>
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#FDEBEB' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Lowest Efficiency
                </p>
                <p className="text-lg font-medium" style={{ color: '#EE1D23' }}>
                  {statistics.minClient?.date ?? '--'}
                </p>
                <p className="text-xl font-bold" style={{ color: '#EE1D23' }}>
                  {statistics.minClient?.efficiency !== null && statistics.minClient?.efficiency !== undefined
                    ? `${Number(statistics.minClient.efficiency).toFixed(0)}%`
                    : '--'}
                </p>
              </div>
            </div>
            <div style={{ width: '100%', height: 500 }}>
              <ChartContent />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BiometricEfficiencyChart;
